<template>
  <div class="main">
    <!-- Navigation bar -->
    <editor-navbar
        :current-channel-id="currentChannel"
        :channels="channels"
        :active-socials="activeSocials"
        :is-new-notif="isNewNotif"
        :collapsing-limit="mobileWidthLimit"
        class="editorNavbar"
        @channelSelected="switchChannel"
        @addAccount="addSocialAccount"
        @removeAccount="handleSelectSocial($event.social, $event.id)"
        @openShortcuts="openShortcutsInfo"
        @openNotifs="isNotifsOpened = true"
        @openCalendar="openModalCalenar"
        @changeDateRange="filterByDateRange"
    />
    <!-- Main app container -->
    <div :id="$options.editorId" class="editorContainer scroll-invisible">
      <!-- Channel player -->
      <div ref="editorPlayerContainer" class="editorPlayer">
        <template v-if="filterActive ? filteredItem.poster : mediaItem.poster" >
          <video-player
              ref="cutPlayer"
              class="edit-form-player"
              :options="playerOptions"
              :thumbnail="filteredItem.poster || mediaItem.poster"
              :src="filteredItem.downloadLink || mediaItem.downloadLink"
          />
          <div class="video-information">
            {{ filteredItem.title || mediaItem.title }}
          </div>
        </template>
        <template v-else>
          <div>Video wasn't found</div>
        </template>
      </div>

      <!-- Videos list -->
      <div class="editorMedia">
        <media-editor
            :media-items="filterActive ? filteredItems : mediaItems"
            :open-modal-express-sharing="openModalExpressSharing"
            @stopUpdateVideos="handleStopUpdateVideos"
            @continueUpdateVideos="handleContinueUpdateVideos"
            @gifRemoved="handleRemoveGIF"
            @cloneItemVideo="cloneVideoItem"
            @sharingRemoved="handleRemoveSharing($event)"
            @playVideoInMediaManager="playVideo"
            @playAudioInMediaManager="playAudio"
        />
      </div>

    </div>
    <!-- Notifications sidebar -->
    <notifications
        class="editorNotifs"
        :timezone="localTimezone"
        :is-opened="isNotifsOpened"
        @opened="isNotifsOpened = true"
        @closed="isNotifsOpened = false"
        @notificationReceived="isNewNotif = true"
        @notificationsRead="isNewNotif = false"
    />
    <!-- Modal forms -->
    <modals-manager
        :channel-id="channelId"
        @selectModal="selectModals = $event"
    />
  </div>
</template>

<script>
import EditorNavbar from "@/components/navbar/EditorNavbar.vue";
import NdvrVideoPlayer from "@/components/media/video/NdvrVideoPlayer.vue";
import MediaEditor from "@/components/media/MediaEditor.vue";
import Notifications from "@/components/notifications/Notifications.vue";
import {mapActions, mapGetters, mapState, mapMutations} from "vuex";
/** modals manager */
import ModalsManager from "@/components/dialog/ModalsManager.vue";

import mediaApi from "@/api/media/media.js";
import {removeSharing} from "@/api/socials/sharings.js";
import {getUrlParamsByKey} from "@/utils/urls.js";
import {
  EDITOR_ID,
  STATISTIC_UPDATE_INTERVAL
} from "@/constants.js";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {setInterval} from "timers";
import {token} from "@/api/params.js";
import VideoPlayer from "@/components/media/video/VideoPlayer";
import defaultAudioPoster from "@/assets/images/audio_channel_default.jpg";

dayjs.extend(utc);
dayjs.extend(timezone);

// Main editor application
export default {
  name: "MediaManager",
  components: {
    EditorNavbar,
    MediaEditor,
    Notifications,
    ModalsManager,
    VideoPlayer,
  },
  reloadTimeout: 15000,
  editorId: EDITOR_ID,
  props: {
    /** initiation channel ID*/
    channelId: {
      type: String,
      required: true
    },
    userInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      playerOptions: {
        autoplay: false,
        controls: true,
        fill: true,
        preload: "none",
        cors: true
      },
      playerWidth: 0,
      playerHeight: 0,
      videosList: [],
      gifsList: [],
      upload: [],
      audiosList: [],
      pageLoaded: false,
      openModalExpressSharing: null,
      valueCheckboxThumbnails: true,
      valueCheckboxRange: false,
      /** loading background */
      loadingInstance: undefined,
      mobileWidthLimit: 992,
      isNotifsOpened: false,
      isNewNotif: false,
      windowWidth: document.body.clientWidth,
      cropSliderMode: "none",
      criticalError: {
        error: null,
        permanent: false
      },
      selectModals: "",
      mediaItemIndex: 0,
      filteredItemIndex: 0,
      filterActive: false,
      filteredVideos: [],
      filteredAudios: [],
      player: {
        title: '',
        poster: '',
        downloadLink: ''
      }
    };
  },
  computed: {
    ...mapGetters(["hasThumbnails"]),
    ...mapState({
      /** stream params */
      stream: state => state.services.stream,
      /** player */
      poster: state => state.services.channelPoster,
      /** API */
      app: state => state.services.app,
      currentChannel: state => state.userData.currentChannel,
      channels: state => state.userData.channels,
      defaultTimezone: state => state.userData.defaultTimezone,
      /** Socials */
      youtube: state => state.socials.youtube,
      facebook: state => state.socials.facebook,
      twitter: state => state.socials.twitter,
      sharings: state => state.socials.sharings,
      /** modals */
      modals: state => state.modals.clipper.MODALS
    }),
    filteredItems() {
      if (this.hasThumbnails) {
        return {
          clips: this.filteredVideos,
          gifs: this.gifsList,
          upload: true
        };
      } else {
        return {
          audios: this.filteredAudios
        };
      }
    },
    // @vuese
    // Current media items lists
    mediaItems() {
      if (this.hasThumbnails) {
        return {
          clips: this.videosList,
          gifs: this.gifsList,
          upload: true
        };
      } else {
        return {
          audios: this.audiosList
        };
      }
    },
    filteredItem() {
      if (this.filteredItems.clips && this.filteredItems.clips.length) {
        return {
          poster: this.filteredItems.clips[this.filteredItemIndex].thumbnailLink,
          downloadLink: {type: "video/mp4", src: this.filteredItems.clips[this.filteredItemIndex].downloadLink},
          title: this.filteredItems.clips[this.filteredItemIndex].tags.title
        }
      } else if (this.filteredItems.audios && this.filteredItems.audios.length) {
        const {channel_cover} = this.channels.find((item) => item.default_stream === this.filteredItems.audios[this.filteredItemIndex].stream)
        return {
          poster: channel_cover || defaultAudioPoster,
          downloadLink: {type: "video/mp4", src: this.filteredItems.audios[this.filteredItemIndex].downloadLink},
          title: this.filteredItems.audios[this.filteredItemIndex].title

        }
      } else {
        return {
          poster: null
        }
      }
    },
    mediaItem() {
      if (this.mediaItems.clips && this.mediaItems.clips.length) {
        return {
          poster: this.mediaItems.clips[this.mediaItemIndex].thumbnailLink,
          downloadLink: {type: "video/mp4", src: this.mediaItems.clips[this.mediaItemIndex].downloadLink},
          title: this.mediaItems.clips[this.mediaItemIndex].tags.title
        }
      } else if (this.mediaItems.audios && this.mediaItems.audios.length) {
        const {channel_cover} = this.channels.find((item) => item.default_stream === this.mediaItems.audios[this.mediaItemIndex].stream)
        return {
          poster: channel_cover ?? defaultAudioPoster,
          downloadLink: {type: "video/mp4", src: this.mediaItems.audios[this.mediaItemIndex].downloadLink},
          title: this.mediaItems.audios[this.mediaItemIndex].title

        }
      } else {
        return {
          poster: null
        }
      }
    },
    // @vuese
    // Using socials list with their labels and connected accounts data
    socialsList() {
      return {
        youtube: this.youtube,
        facebook: this.facebook,
        twitter: this.twitter
      };
    },
    // @vuese
    // List of socials that have connected accounts
    activeSocials() {
      const resultSocials = {};
      for (const socialName in this.socialsList) {
        const social = this.socialsList[socialName];
        if (social.accounts.length > 0) {
          resultSocials[socialName] = social;
        }
      }

      return resultSocials;
    },
    // @vuese
    // User timezone name
    localTimezone: {
      cache: false,
      get() {
        return dayjs.tz.guess();
      }
    },
  },
  watch: {
    "criticalError.error"() {
      const {error, permanent} = this.criticalError;
      if (error) {
        this.showCriticalError(error);

        if (!permanent) {
          setTimeout(() => {
            //window.location.reload();
          }, this.$options.reloadTimeout);
        }
      } else {
        this.hideCriticalError();
      }
    },
    // @vuese
    // Compare the old ID with the new one
    currentChannel(newChannel, oldChannel) {

    },
    channelId() {
      location.reload();
    },

  },
  created() {
    this.setMediaManagerMode({data: true})
    /** check watch link to route user to video */
        // eslint-disable-next-line no-undef
    const storagePrefix = process.env.VUE_APP_STORAGE_PREFIX;
    if (storagePrefix == undefined) {
      this.criticalError = {
        error: "Setting error: storage prefix is not set",
        permanent: true
      };
      return;
    }

    const watchPath = getUrlParamsByKey("watch")[0];
    if (watchPath) {
      const decodedPath = decodeURIComponent(watchPath);
      const watchPattern = /^[a-z0-9./_]+$/i;
      if (watchPattern.test(decodedPath)) {
        document.location.href = `${storagePrefix}/${watchPath}`;
      } else {
        this.criticalError = {
          error: "Request error: watch link is not valid",
          permanent: true
        };
      }
    }
  },
  async mounted() {
    if (this.criticalError.error) {
      return;
    }

    try {
      /** media height fixing */
      window.addEventListener(
          "resize",
          () => (this.windowWidth = document.body.clientWidth)
      );

      /** show download spinner */
          // this.showLoading("Loading");

      const userInfo = this.userInfo;
      if (userInfo.channels.length !== 0) {
        //если нет чанела поставить нулевой

        this.initializeUserData({
          userData: userInfo,
          channelId: null,
        });
        if (this.defaultTimezone) {
          this.initTimezone({
            defaultTimezone: this.defaultTimezone,
            channelId: null
          });
        } else {
          this.initTimezone("UTC");
        }

        // this.hideLoading();

        this.subscribeToMedia();

        /** init socials */
      } else {
        /** if token valid and we got empty list of channels */
        throw Error("No Streams allowed");
      }
    } catch (e) {
      /** if token invalid */
      // this.hideLoading();
      this.criticalError = {
        error: `Clipper Error : ${e}, please try again later`,
        permanent: false
      };
    }
  },
  methods: {
    ...mapActions("socials", [
      "addAccount",
      "deleteAccount",
      "setSharing",
      "excludeSharing"
    ]),
    ...mapActions("timezone", ["initTimezone"]),
    ...mapActions("modals", ["openClipperModal"]),
    ...mapActions({
      initializeUserData: "initializeUserData",
      setChannel: "setChannel",
      setMediaManagerMode: "setMediaManagerMode"
    }),
    playAudio(audio_id) {

      if (this.filterActive) {
        this.filteredItemIndex = this.filteredAudios.findIndex((item) => item.id === audio_id)

      } else {
        this.mediaItemIndex = this.audiosList.findIndex((item) => item.id === audio_id)
      }

    },
    playVideo(videoId) {
      if (this.filterActive) {
        this.filteredItemIndex = this.filteredVideos.findIndex((item) => item.id === videoId)
      } else {
        this.mediaItemIndex = this.videosList.findIndex((item) => item.id === videoId)
      }

    },
    filterByDateRange(date) {
      if (date) {
        this.filterActive = true
        const date_1 = date[0]
        const date_2 = date[1]
        this.subscribeToMedia()
        if (this.hasThumbnails) {
          this.filteredVideos = this.videosList.filter((item) => {
            const date = new Date(item.createdAt * 1000)
            date.setHours(0, 0, 0, 0)
            return Math.floor(date.getTime() / 1000)
                >= Math.floor(date_1.getTime() / 1000) &&
                Math.floor(date.getTime() / 1000) <= Math.floor(date_2.getTime() / 1000)
          })
        } else {
          this.filteredAudios = this.audiosList.filter((item) => item.createdAt
              > Math.floor(date_1.getTime() / 1000) &&
              item.createdAt < Math.floor(date_2.getTime() / 1000))
        }
      } else {
        this.filterActive = false
      }

    },
    subscribeToMedia() {
      if (this.hasThumbnails) {
        this.subscribeToVideos();
        this.subscribeToGIFs();
      } else {
        this.subscribeToAudios();
      }

    },
    cloneVideoItem(arrVideoInfo) {
      console.log('clone')
      // this.videosList.push(arrVideoInfo);
    },
    unsubscribeFromMedia() {
      if (this.hasThumbnails) {
        mediaApi.unsubscribeFromVideoUpdates();
        mediaApi.unsubscribeFromGIFUpdates();
      } else {
        mediaApi.unsubscribeFromAudioUpdates();
      }
    },
    // @vuese
    // Enable videos updating
    subscribeToVideos() {
      mediaApi.subscribeToVideoUpdates(
          this.app,
          this.stream,
          videos => {
            if (!this.filterActive) {
              this.videosList = videos.slice();

              // Update sharings data when video updated
              this.videosList.forEach(v => {
                v.sharings.forEach(s =>
                    this.setSharing({
                      id: s.id,
                      videoId: v.id,
                      social: s.target,
                      status: s.status,
                      link: s.link ? s.link : null,
                      percentage: s.percentage ? s.percentage : null,
                      socialVideoId: s.socialVideoId,
                      accountId: s.userSocialAccountId,
                      publishTime: s.startPublishingTime
                    })
                );
              });
              this.videosList.forEach((item) => {
                if(item.thumbnailLink){
                  // item.thumbnailLink = item.storageUrl + '/' + item.thumbnailLink
                } else {
                  let {channel_cover} = this.channels.find((channel) => channel.default_stream === item.stream)
                  item.thumbnailLink = channel_cover ?? defaultAudioPoster
                }
                // item.downloadLink = item.storageUrl + '/' + item.downloadLink
              })


            }
          },
          e => {
            console.error(e);
            this.showError("Fetch Videos List Error", e);
          }
      );
    },
    // @vuese
    // Restart video list updating
    handleContinueUpdateVideos() {
      this.subscribeToVideos();
    },
    // @vuese
    // Stop video list updating
    handleStopUpdateVideos() {
      mediaApi.unsubscribeFromVideoUpdates();
    },
    // @vuese
    // Enable GIFs updating
    subscribeToGIFs() {
      mediaApi.subscribeToGIFUpdates(
          this.app,
          this.stream,
          gifs => {
            this.gifsList = gifs;
          },
          e => {
            console.error(e);
            this.showError("Fetch GIFs List Error", e);
          }
      );
    },
    // @vuese
    // Enable audios updating
    subscribeToAudios() {
      mediaApi.subscribeToAudioUpdates(this.app, this.stream, {
        callback: audios => {
          this.audiosList = audios;
          this.audiosList.forEach((item) => {
            item.downloadLink = item.storageUrl + '/' + item.downloadLink
            item.thumbnailLink = item.storageUrl + '/' + item.channel_cover
          })

        },
        error: e => {
          console.error(e);
          this.showError("Fetch Audios List Error", e);
        }
      });
    },
    // @vuese
    // Switch current channel
    // @arg value - new channel ID
    async switchChannel(value) {
      if (value === this.currentChannel) {
        return;
      }
      this.mediaItemIndex = 0
      this.filterActive = false
      this.unsubscribeFromMedia();
      /** this.showLoading("Loading");*/
      this.setChannel(value);
      this.subscribeToMedia();
    },


    // @vuese
    // Show disconnect account confiration
    // @arg social - Social name (facebook/youtube/twitter) <br>
    // @arg id - universal social account identifier
    handleSelectSocial(social, id) {
      this.$confirm(
          `Do you want to remove this account? It will remove all connecting sharing info.`,
          "Disconnect",
          {
            confirmButtonText: "Disconnect",
            cancelButtonText: "Cancel"
          }
      ).then(() => this.handleAccountDisconnecting(social, id));
    },
    // @vuese
    // Try to disconnect account
    // @arg social - account's social <br>
    // @arg id - account's ID
    async handleAccountDisconnecting(social, id) {
      try {
        await this.deleteAccount({social, id});
        console.log(
            `${social} account (id:${id}) was disconnected succesfully`
        );
        this.showSuccess("Success", "Account has disabled");
        this.sharingsdate
            .filter(s => s.accountId == id)
            .forEach(s => {
              this.handleRemoveSharing({videoId: s.videoId, sharingId: s.id});
            });
      } catch (e) {
        console.error(e);
        this.showError("Disconnecting Error", e);
      }
    },
    // @vuese
    // Remove sharing data
    // @arg videoId - shared video ID <br>
    // @arg sharingId - sharing data ID
    async handleRemoveSharing({videoId, sharingId}) {
      try {
        await removeSharing(videoId, sharingId);
        this.excludeSharing(sharingId);
      } catch (e) {
        this.showError("Sharing Record Remove Failed", e);
      }
    },
    // @vuese
    // Connect new socials account
    // @arg social (youtube/facebook/twitter)
    async addSocialAccount(social) {
      try {
        await this.addAccount(social);
        this.showSuccess("Success", "Account has connected");
      } catch (e) {
        if (e.error !== "popup_closed_by_user") {
          this.showError("Socials Initialize Error", e);
          console.error(e);
        }
      }
    },
    // @vuese
    // Open shortcuts helper form
    openShortcutsInfo() {
      this.openClipperModal({
        modal: this.modals.SHORTCUTS,
        data: null
      });
    },
    openModalCalenar() {
      this.openClipperModal({
        modal: this.modals.CALENDAR,
        data: null
      });
    },
    // @vuese
    // Remove GIF from local data (not from server)
    // @arg id - GIF ID
    handleRemoveGIF(id) {
      const index = this.gifsList.findIndex(element => {
        return id == element.id;
      });
      this.gifsList.splice(index, 1);
    },
    // @vuese
    // Show error notification
    // @arg title - main subject <br>
    // @arg message - detalaized description
    showError(title, message) {
      this.$notify({
        group: "foo",
        position: "bottom right",
        type: "error",
        title: title,
        text: message
      });
    },
    // @vuese
    // Show success notification
    // @arg title - main subject <br>
    // @arg message - detalaized description
    showSuccess(title, message) {
      this.$notify({
        group: "foo",
        position: "bottom right",
        type: "success",
        title: title,
        text: message
      });
    },
    // @vuese
    // Show overlay with warning icon and custom message
    // @arg message - message to show
    showCriticalError(message) {
      this.errorInstance = this.$loading({
        lock: true,
        text: message,
        spinner: "el-icon-warning-outline",
        background: "rgba(0, 0, 0, 1)"
      });
    },
    // @vuese
    // Hide fullscreen error overlay
    hideCriticalError() {
      this.errorInstance.close();
    },
    // @vuese
    // Show fullscreen loading spinner
    // @arg message - loading text message
    showLoading(message) {
      this.loadingInstance = this.$loading({
        lock: true,
        text: message,
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 1)"
      });
    },
    // @vuese
    // Hide fullscreen loading spinner
    hideLoading() {
      this.loadingInstance.close();
    },

  }
};
</script>

<style lang="scss">
body {
  overflow-x: hidden;
}

.main {
  height: 100vh;
  overflow: hidden;
}

.editorNavbar {
  width: 100%;
  min-height: 60px;
}

.logo {
  max-width: 125px;
  max-height: 250px;
  margin-top: 10px;
  margin-bottom: 5px;
  display: flex;
  padding-left: 20px;
  justify-self: start;
}

.editorContainer {
  height: calc(100% - 60px);

  display: grid;
  grid-template-columns: 60% 40%;
  grid-template-rows: 1fr 30px;
  max-width: 100vw;
  max-height: 100vh;
  justify-items: stretch;
  grid-template-areas:
    "player media"
    "timeline controls";

  background-color: #233d4e;
  overflow-x: hidden;
}

.preview .playerContainer {
  width: 67vw !important;
}

@keyframes dots {
  0%,
  20% {
    color: rgba(0, 0, 0, 0);
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: white;
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  80%,
  100% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 white;
  }
}

.editorPlayer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  grid-area: player;
  background-color: transparent;
  color: white;
  z-index: var(--player-z);
}

.editorMedia {
  grid-area: media;
  border: solid 1px #423f3f;
  background-color: #233d4e;
  color: white;
  display: block;
  overflow: hidden;
  position: relative;
}

.editorCropControls {
  grid-area: controls;
  border: solid 1px var(--light-grey);
  background-color: #233d4e;
  color: white;
}

.editorTimeline {
  grid-area: timeline;

  width: 100%;
  box-sizing: border-box;

  display: flex;
  align-items: center;

  border: solid 1px var(--light-grey);
  background-color: #233d4e;
  color: white;
  padding: 5px;

  &__timeline {
    width: 100%;
    flex-grow: 1;
  }
}

.editorNotifs {
  z-index: var(--notifs-z);
}

/** media queries */
@media only screen and (max-width: 767px) {
  .editorContainer {
    grid-template-rows: 1fr 230px;
    max-width: 100vw;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: unset;
    // position: relative;
  }
}

@media only screen and (min-width: 768px) {
  .editorTimeline {
    max-height: 160px;
  }
  .editorCropControls {
    max-height: 170px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: unset;
    // position: relative;
  }
}

@media only screen and (max-width: 991px) {
  .editorNavbar {
    position: fixed;
    top: 0;
    z-index: var(--navbar-z);
  }
  .editorContainer {
    margin-top: 60px;
    grid-template-columns: 100vw;
    grid-template-areas:
      "player"
      "timeline"
      "controls"
      "media";
  }
  .editorPlayer {
    /** 16/9 + player controls panel height */
    height: calc(56.25vw + 152px);
  }
}

@media only screen and (min-width: 992px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 160px;
  }
  .editorCropControls {
    max-height: 170px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

@media only screen and (min-width: 1200px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 150px;
  }
  .editorTimeline {
    max-height: 160px;
  }
  .editorCropControls {
    max-height: 170px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

@media only screen and (min-width: 1920px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 300px;
  }
  .editorCropControls {
    max-height: 300px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

@media only screen and (min-width: 1921px) {
  .editorTimeline {
    max-height: 300px;
  }
  .editorCropControls {
    max-height: 300px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

.video-information {
  text-align: left;
  margin: 15px 0 0 15px;
}
</style>

<style>
:root {
  /** colors */
  --dark-grey: #3d3d3d;
  --grey: #5b5b5b;
  --light-grey: #878787;

  /** layers for modals */
  --modal-dropdown-z: 3200;
  --modal-z: 3150;
  --modal-backdrop-z: 3100;

  /** layer for notifications panel */
  --notifs-z: 3000;

  /** navigation bar layers */
  --navbar-dropdown-z: 2050;
  --navbar-z: 2000;

  --video-thumb-overlay-z: 50;

  /** player layers */
  --player-z: 50;
  --player-controls-z: 100;

  /** areas */
  --edit-area-z: 50;

  /** timeine layers */
  --timeline-popup-z: 300;
  --timeline-cursor-z: 120;
  --timeline-break-z: 20;

  /** left handle wrapper should to have more z-index to prevent ovelaying from right handle wrapper */
  --timeline-left-handle-z: 16;
  --timeline-right-handle-z: 15;
  --timeline-slider-z: 15;
  --timeline-faded-z: 20;
  --timeline-label-z: 10;
  --timeline-duration-z: 30;
  --timeline-button-z: 170;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: var(--light-grey);
  height: 100vh;
}

.el-loading-spinner .el-loading-text {
  white-space: pre-wrap;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

/* Scrollbars */
/* width */
.scrollable ::-webkit-scrollbar {
  width: 15px;
}

/* .scroll-invisible::-webkit-scrollbar {
  display: none;
} */

.scroll-invisible {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

/* Track */
.scrollable ::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

/* Handle */
.scrollable ::-webkit-scrollbar-thumb {
  background: var(--dark-grey);
  border-radius: 10px;
}

/* Handle on hover */
.scrollable ::-webkit-scrollbar-thumb:hover {
  background: #2d2d2d;
  border-radius: 10px;
}

/** modal */
.modal-select-dropdown {
  z-index: var(--modal-dropdown-z) !important;
}
</style>



























































































































































